import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SEARCH_QUERY } from '../../graphql/resolvers/searchQueries/search.gql';
import styles from './directMessages.module.scss';

interface SearchAuthor {
  PK?: string;
  username?: string;
  displayName?: string;
}

interface Props {
  /** Called with the picked user's id + username to open (and, on first send, create) the thread. */
  onPick: (otherUserId: string, username: string) => void;
}

// Author docs are indexed from UsersTable, so PK is `USER#<userId>` — mirrors the
// Search page deriving a post id from `POST#<id>`.
const idFromAuthorPK = (pk?: string): string =>
  typeof pk === 'string' && pk.startsWith('USER#') ? pk.slice(5) : '';

/**
 * TRIL-402: "New message" — search people and start a DM with the picked user,
 * straight from the /direct-messages page (no need to find their hovercard first).
 */
export function NewMessageComposer({ onPick }: Props) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const query = q.trim();

  const { data, loading } = useQuery(SEARCH_QUERY, {
    variables: { query, limit: 8 },
    skip: query.length < 2,
    fetchPolicy: 'no-cache',
  });

  const authors: SearchAuthor[] = (data?.search?.authors ?? []).filter(
    (a: SearchAuthor) =>
      a.username &&
      a.username !== user?.username &&
      idFromAuthorPK(a.PK) !== user?.id,
  );

  const close = () => {
    setOpen(false);
    setQ('');
  };

  const pick = (a: SearchAuthor) => {
    const otherId = idFromAuthorPK(a.PK);
    if (!otherId || !a.username) return;
    onPick(otherId, a.username);
    close();
  };

  return (
    <div className={styles.composeWrap}>
      <button
        type="button"
        className={styles.newMsgBtn}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {open ? 'Cancel' : '＋ New message'}
      </button>

      {open && (
        <div className={styles.composePanel}>
          <input
            className={styles.composeInput}
            autoFocus
            placeholder="Search people…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {query.length >= 2 && (
            <ul className={styles.composeResults}>
              {loading && <li className={styles.composeHint}>Searching…</li>}
              {!loading && authors.length === 0 && (
                <li className={styles.composeHint}>No people found.</li>
              )}
              {authors.map((a) => (
                <li key={a.PK}>
                  <button
                    type="button"
                    className={styles.composeResult}
                    onClick={() => pick(a)}
                  >
                    <span className={styles.composeAvatar} aria-hidden="true">
                      {(a.username ?? '?').charAt(0).toUpperCase()}
                    </span>
                    <span className={styles.composeName}>
                      {a.displayName || a.username}
                      <span className={styles.composeHandle}>
                        @{a.username}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
